import {
  CRISIS_90839_MAX_MINUTES,
  CRISIS_90839_MIN_MINUTES,
  CRISIS_90840_FIRST_UNIT_MIN_MINUTES,
  DIAGNOSTIC_90791_MAX_MINUTES,
  DIAGNOSTIC_90791_MIN_MINUTES,
  EM_CODES_ESTABLISHED,
  EM_CODES_NEW,
  FAMILY_THERAPY_MIN_MINUTES,
  GROUP_THERAPY_90853_MAX_MINUTES,
  GROUP_THERAPY_90853_MIN_MINUTES,
  PSYCHOTHERAPY_ADD_ON_90833_MIN_MINUTES,
  PSYCHOTHERAPY_MIN_BILLABLE_MINUTES,
} from './constants';
import {
  calculate90840Units,
  durationMateriallyDiffersFrom90792Standard,
  emCodeRank,
  getExpectedEmCode,
  getExpectedPsychotherapyCodeByDuration,
  is96127Eligible,
  isInteractiveComplexitySupportedPrimaryPresent,
  psychotherapyDurationMessage,
  psychotherapyKeepMessage,
} from './helpers';
import type { EncounterScrubberInput, EncounterScrubberResult } from './types';

function result(
  partial: Omit<EncounterScrubberResult, 'code'> & { code: string },
): EncounterScrubberResult {
  return partial;
}

function evaluatePsychotherapyDurationCode(
  input: EncounterScrubberInput,
  code: '90832' | '90834' | '90837',
): EncounterScrubberResult {
  const { durationMinutes } = input;
  const expected = getExpectedPsychotherapyCodeByDuration(durationMinutes);

  if (expected === code) {
    return result({
      code,
      action: 'keep',
      severity: 'info',
      message: psychotherapyKeepMessage(code, durationMinutes),
    });
  }

  if (expected) {
    return result({
      code,
      action: 'suggest_replace',
      severity: 'warning',
      message: psychotherapyDurationMessage(code, durationMinutes, expected),
      suggestedCode: expected,
    });
  }

  if (
    durationMinutes != null &&
    durationMinutes < PSYCHOTHERAPY_MIN_BILLABLE_MINUTES
  ) {
    return result({
      code,
      action: 'billing_review',
      severity: 'warning',
      message: `${code} was selected, but duration is below ${PSYCHOTHERAPY_MIN_BILLABLE_MINUTES} minutes. No psychotherapy CPT is suggested; billing review needed.`,
    });
  }

  return result({
    code,
    action: 'billing_review',
    severity: 'warning',
    message: `${code} is selected; duration could not be evaluated for psychotherapy billing.`,
  });
}

function evaluateEmCode(input: EncounterScrubberInput, code: string): EncounterScrubberResult {
  const { patientStatus, mdmLevel } = input;
  const expected = getExpectedEmCode(patientStatus, mdmLevel);

  if (!patientStatus || !mdmLevel) {
    return result({
      code,
      action: 'billing_review',
      severity: 'warning',
      message: `${code} was selected, but patient status and MDM are not documented.`,
    });
  }

  if (expected === code) {
    return result({
      code,
      action: 'keep',
      severity: 'info',
      message: `${code} matches patient status (${patientStatus}) and MDM (${mdmLevel}).`,
    });
  }

  if (!expected) {
    return result({
      code,
      action: 'billing_review',
      severity: 'warning',
      message: `${code} was selected, but MDM level could not be mapped.`,
    });
  }

  const selectedRank = emCodeRank(code, patientStatus);
  const expectedRank = emCodeRank(expected, patientStatus);

  if (selectedRank < 0) {
    return result({
      code,
      action: 'suggest_replace',
      severity: 'warning',
      message: `${code} was selected, but patient status and MDM indicate ${expected}.`,
      suggestedCode: expected,
    });
  }

  if (selectedRank > expectedRank) {
    return result({
      code,
      action: 'suggest_replace',
      severity: 'warning',
      message: `${code} was selected, but MDM supports a lower level. Suggest ${expected}.`,
      suggestedCode: expected,
    });
  }

  return result({
    code,
    action: 'suggest_replace',
    severity: 'warning',
    message: `${code} was selected, but patient status and MDM indicate ${expected}. Selected E/M code does not match documented MDM.`,
    suggestedCode: expected,
  });
}

function evaluate90791(input: EncounterScrubberInput): EncounterScrubberResult {
  const { durationMinutes, firstSessionWithClientOrCase, newTherapistFirstSessionWithClientOrCase } =
    input;
  const isFirstSession =
    firstSessionWithClientOrCase === true || newTherapistFirstSessionWithClientOrCase === true;

  if (durationMinutes != null) {
    if (
      durationMinutes < DIAGNOSTIC_90791_MIN_MINUTES ||
      durationMinutes > DIAGNOSTIC_90791_MAX_MINUTES
    ) {
      return result({
        code: '90791',
        action: 'billing_review',
        severity: 'warning',
        message: `90791 is selected, but duration is ${durationMinutes} minutes. Expected ${DIAGNOSTIC_90791_MIN_MINUTES}–${DIAGNOSTIC_90791_MAX_MINUTES} minutes; billing review needed.`,
      });
    }
  }

  if (isFirstSession) {
    return result({
      code: '90791',
      action: 'keep',
      severity: 'info',
      message:
        '90791 is appropriate for a first session with the client/case or a new therapist\'s first session.',
    });
  }

  if (
    firstSessionWithClientOrCase === false &&
    newTherapistFirstSessionWithClientOrCase === false
  ) {
    return result({
      code: '90791',
      action: 'keep',
      severity: 'warning',
      message:
        '90791 should generally be used for the first session with the client/case or a new therapist\'s first session with the client/case.',
    });
  }

  return result({
    code: '90791',
    action: 'keep',
    severity: 'info',
    message: '90791 is selected.',
  });
}

function evaluate90792(input: EncounterScrubberInput): EncounterScrubberResult {
  const durationWarning = durationMateriallyDiffersFrom90792Standard(input.durationMinutes);

  if (input.diagnosticEvaluationDocumentationPresent === false) {
    if (durationWarning) {
      return result({
        code: '90792',
        action: 'billing_review',
        severity: 'warning',
        message:
          '90792 is selected, but diagnostic evaluation documentation is missing and duration materially differs from the standard 60 minutes. Billing review needed.',
      });
    }
    return result({
      code: '90792',
      action: 'keep',
      severity: 'warning',
      message: '90792 is selected, but diagnostic evaluation documentation is missing.',
    });
  }

  if (durationWarning) {
    return result({
      code: '90792',
      action: 'billing_review',
      severity: 'warning',
      message: `90792 is selected, but duration (${input.durationMinutes} minutes) materially differs from the standard 60 minutes. Billing review needed.`,
    });
  }

  if (input.diagnosticEvaluationDocumentationPresent === true) {
    return result({
      code: '90792',
      action: 'keep',
      severity: 'info',
      message: '90792 is supported by diagnostic evaluation documentation.',
    });
  }

  return result({
    code: '90792',
    action: 'keep',
    severity: 'info',
    message: '90792 is selected.',
  });
}

function evaluate90833(input: EncounterScrubberInput): EncounterScrubberResult {
  if (input.currentCptCodes.includes('90792')) {
    return result({
      code: '90833',
      action: 'flag_unsupported',
      severity: 'warning',
      message: '90833 cannot be billed with intake code 90792. Flagging as unsupported.',
    });
  }

  if (input.psychotherapyAddOn === false) {
    return result({
      code: '90833',
      action: 'suggest_remove',
      severity: 'warning',
      message: '90833 is selected, but psychotherapy add-on is not documented. Suggest removing 90833.',
    });
  }

  const duration = input.durationMinutes;
  if (duration != null && duration < PSYCHOTHERAPY_ADD_ON_90833_MIN_MINUTES) {
    return result({
      code: '90833',
      action: 'suggest_remove',
      severity: 'warning',
      message: `90833 requires at least ${PSYCHOTHERAPY_ADD_ON_90833_MIN_MINUTES} minutes of psychotherapy add-on time. Suggest removing 90833.`,
    });
  }

  if (input.psychotherapyAddOn === true) {
    return result({
      code: '90833',
      action: 'keep',
      severity: 'info',
      message: '90833 is documented as a psychotherapy add-on with sufficient duration.',
    });
  }

  return result({
    code: '90833',
    action: 'suggest_remove',
    severity: 'warning',
    message: '90833 is selected, but psychotherapy add-on status is not documented.',
  });
}

function evaluate90839(input: EncounterScrubberInput): EncounterScrubberResult {
  const duration = input.durationMinutes;

  if (duration != null && duration < CRISIS_90839_MIN_MINUTES) {
    return result({
      code: '90839',
      action: 'billing_review',
      severity: 'warning',
      message: `90839 requires at least ${CRISIS_90839_MIN_MINUTES} minutes. Duration is ${duration} minutes; billing review needed.`,
    });
  }

  if (input.crisisDocumentationPresent === false) {
    const suggested = getExpectedPsychotherapyCodeByDuration(duration);
    if (suggested) {
      return result({
        code: '90839',
        action: 'suggest_replace',
        severity: 'warning',
        message: `90839 is selected, but crisis documentation is missing. Suggest psychotherapy code ${suggested} by duration.`,
        suggestedCode: suggested,
      });
    }
    return result({
      code: '90839',
      action: 'billing_review',
      severity: 'warning',
      message:
        '90839 is selected, but crisis documentation is missing and duration is below psychotherapy minimum. Billing review needed.',
    });
  }

  if (input.crisisDocumentationPresent === true) {
    if (
      duration != null &&
      duration >= CRISIS_90839_MIN_MINUTES &&
      duration <= CRISIS_90839_MAX_MINUTES
    ) {
      return result({
        code: '90839',
        action: 'keep',
        severity: 'info',
        message: `90839 is supported by crisis documentation and duration (${duration} minutes).`,
      });
    }

    if (duration != null && duration >= CRISIS_90840_FIRST_UNIT_MIN_MINUTES) {
      return result({
        code: '90839',
        action: 'keep',
        severity: 'info',
        message: `90839 is supported by crisis documentation. Evaluate 90840 add-on units for ${duration} minutes.`,
      });
    }
  }

  return result({
    code: '90839',
    action: 'keep',
    severity: 'warning',
    message: '90839 is selected; crisis documentation or duration may be incomplete.',
  });
}

function evaluate90840(input: EncounterScrubberInput): EncounterScrubberResult {
  const has90839 = input.currentCptCodes.includes('90839');
  const units = calculate90840Units(input.durationMinutes);

  if (!has90839) {
    return result({
      code: '90840',
      action: 'flag_unsupported',
      severity: 'warning',
      message: '90840 requires 90839 to be present. Flagging as unsupported.',
    });
  }

  if (units === 0) {
    return result({
      code: '90840',
      action: 'suggest_remove',
      severity: 'warning',
      message: `90840 is selected, but total crisis duration is below ${CRISIS_90840_FIRST_UNIT_MIN_MINUTES} minutes. Suggest removing 90840.`,
    });
  }

  return result({
    code: '90840',
    action: 'keep',
    severity: 'info',
    message: `90840 is supported with ${units} unit${units === 1 ? '' : 's'} based on duration.`,
    units,
  });
}

function evaluate90846(input: EncounterScrubberInput): EncounterScrubberResult {
  const duration = input.durationMinutes;

  if (duration != null && duration < FAMILY_THERAPY_MIN_MINUTES) {
    return result({
      code: '90846',
      action: 'billing_review',
      severity: 'warning',
      message: `90846 requires at least ${FAMILY_THERAPY_MIN_MINUTES} minutes. Duration is ${duration} minutes; no family psychotherapy CPT suggested.`,
    });
  }

  if (input.identifiedPatientPresent === true) {
    return result({
      code: '90846',
      action: 'suggest_replace',
      severity: 'warning',
      message: '90846 was selected, but the identified patient is present. Suggest 90847.',
      suggestedCode: '90847',
    });
  }

  if (
    input.identifiedPatientPresent === false &&
    input.familyParticipantPresent === true &&
    duration != null &&
    duration >= FAMILY_THERAPY_MIN_MINUTES
  ) {
    return result({
      code: '90846',
      action: 'keep',
      severity: 'info',
      message: '90846 is appropriate — family participant present without identified patient.',
    });
  }

  return result({
    code: '90846',
    action: 'keep',
    severity: 'warning',
    message: '90846 is selected; family participant presence may not be documented.',
  });
}

function evaluate90847(input: EncounterScrubberInput): EncounterScrubberResult {
  const duration = input.durationMinutes;

  if (duration != null && duration < FAMILY_THERAPY_MIN_MINUTES) {
    return result({
      code: '90847',
      action: 'billing_review',
      severity: 'warning',
      message: `90847 requires at least ${FAMILY_THERAPY_MIN_MINUTES} minutes. Duration is ${duration} minutes; no family psychotherapy CPT suggested.`,
    });
  }

  if (input.identifiedPatientPresent === false) {
    return result({
      code: '90847',
      action: 'suggest_replace',
      severity: 'warning',
      message: '90847 was selected, but the identified patient is not present. Suggest 90846.',
      suggestedCode: '90846',
    });
  }

  if (input.identifiedPatientPresent === true && duration != null && duration >= FAMILY_THERAPY_MIN_MINUTES) {
    return result({
      code: '90847',
      action: 'keep',
      severity: 'info',
      message: '90847 is appropriate — identified patient is present.',
    });
  }

  return result({
    code: '90847',
    action: 'keep',
    severity: 'warning',
    message: '90847 is selected; identified patient presence is not documented.',
  });
}

function evaluate90853(input: EncounterScrubberInput): EncounterScrubberResult {
  const duration = input.durationMinutes;

  if (input.patientGroupEncounterAttended === false) {
    return result({
      code: '90853',
      action: 'flag_unsupported',
      severity: 'warning',
      message:
        '90853 is selected, but the patient did not attend the group encounter. No reliable appointment mapping — flagging for review.',
    });
  }

  if (
    duration != null &&
    (duration < GROUP_THERAPY_90853_MIN_MINUTES || duration > GROUP_THERAPY_90853_MAX_MINUTES)
  ) {
    return result({
      code: '90853',
      action: 'billing_review',
      severity: 'warning',
      message: `90853 requires ${GROUP_THERAPY_90853_MIN_MINUTES}–${GROUP_THERAPY_90853_MAX_MINUTES} minutes. Duration is ${duration} minutes; billing review needed.`,
    });
  }

  if (input.patientGroupEncounterAttended === true) {
    return result({
      code: '90853',
      action: 'keep',
      severity: 'info',
      message: '90853 is supported — patient attended the group encounter with appropriate duration.',
    });
  }

  return result({
    code: '90853',
    action: 'flag_unsupported',
    severity: 'warning',
    message: '90853 is selected; group encounter attendance is not documented.',
  });
}

function evaluate96127(input: EncounterScrubberInput, isSelected: boolean): EncounterScrubberResult | null {
  if (!isSelected) {
    if (is96127Eligible(input)) {
      return result({
        code: '96127',
        action: 'suggest_add',
        severity: 'info',
        message:
          '96127 can be added because the payer code list includes 96127 and an assessment was performed on the same day as the session.',
        suggestedCode: '96127',
      });
    }
    return null;
  }

  if (!input.payerCodesList.includes('96127')) {
    return result({
      code: '96127',
      action: 'do_not_add',
      severity: 'info',
      message: '96127 is selected, but the payer code list does not include 96127.',
    });
  }
  if (input.assessmentPerformedSameDateAsSession !== true) {
    return result({
      code: '96127',
      action: 'do_not_add',
      severity: 'warning',
      message: '96127 is selected, but no assessment was performed on the same date as the session.',
    });
  }
  return result({
    code: '96127',
    action: 'keep',
    severity: 'info',
    message: '96127 is supported — payer allows the code and assessment occurred on the same date.',
  });
}

/** 96138 — intentionally no configured checks beyond provider access. */
function evaluate96138(): EncounterScrubberResult {
  return result({
    code: '96138',
    action: 'keep',
    severity: 'info',
    message: '96138 is selected; no configured scrubber checks apply.',
  });
}

function evaluate96146(input: EncounterScrubberInput): EncounterScrubberResult {
  const { payerPlanType, payerName, prior96146CountForClient } = input;
  const isMediCal = payerPlanType === 'Medi-Cal';
  const isBsPromise = payerName === 'BS Promise';

  if (isMediCal && prior96146CountForClient === 0) {
    return result({
      code: '96146',
      action: 'keep',
      severity: 'info',
      message: '96146 is supported for Medi-Cal when no prior 96146 has been billed for this client.',
    });
  }

  if (isMediCal && prior96146CountForClient != null && prior96146CountForClient > 0) {
    if (is96127Eligible(input)) {
      return result({
        code: '96146',
        action: 'suggest_replace',
        severity: 'warning',
        message:
          '96146 has been billed previously for this Medi-Cal client. Suggest 96127 if eligible.',
        suggestedCode: '96127',
      });
    }
    return result({
      code: '96146',
      action: 'billing_review',
      severity: 'warning',
      message:
        '96146 has been billed previously for this Medi-Cal client and 96127 is not eligible. Billing review needed.',
    });
  }

  if (isBsPromise) {
    if (is96127Eligible(input)) {
      return result({
        code: '96146',
        action: 'suggest_replace',
        severity: 'warning',
        message: '96146 may not be appropriate for BS Promise. Suggest 96127 if eligible.',
        suggestedCode: '96127',
      });
    }
    return result({
      code: '96146',
      action: 'billing_review',
      severity: 'warning',
      message: '96146 may not be appropriate for BS Promise and 96127 is not eligible. Billing review needed.',
    });
  }

  if (!isMediCal && !isBsPromise) {
    return result({
      code: '96146',
      action: 'keep',
      severity: 'info',
      message: '96146 is selected; payer-specific restrictions do not apply.',
    });
  }

  return result({
    code: '96146',
    action: 'keep',
    severity: 'info',
    message: '96146 is selected.',
  });
}

function evaluate90785(input: EncounterScrubberInput, isSelected: boolean): EncounterScrubberResult | null {
  const supportedPrimary = isInteractiveComplexitySupportedPrimaryPresent(input.currentCptCodes);

  if (!isSelected) {
    if (input.interactiveComplexityFactorDocumented === true && supportedPrimary) {
      return result({
        code: '90785',
        action: 'suggest_add',
        severity: 'info',
        message:
          '90785 can be added because interactive complexity is documented and a supported primary code is present.',
        suggestedCode: '90785',
      });
    }
    return null;
  }

  if (input.interactiveComplexityFactorDocumented === false) {
    return result({
      code: '90785',
      action: 'suggest_remove',
      severity: 'warning',
      message:
        '90785 is selected, but no interactive complexity factor is documented. Suggest removing 90785.',
    });
  }

  if (input.interactiveComplexityFactorDocumented === true && !supportedPrimary) {
    return result({
      code: '90785',
      action: 'flag_unsupported',
      severity: 'warning',
      message:
        '90785 requires a supported primary CPT (90791, 90792, 90832, 90834, 90837, or 90853). Flagging as unsupported.',
    });
  }

  return result({
    code: '90785',
    action: 'keep',
    severity: 'info',
    message: '90785 is supported by documented interactive complexity and a supported primary code.',
  });
}

function evaluate99241(input: EncounterScrubberInput): EncounterScrubberResult {
  if (input.legacyConsultCodeEnabled === true && input.payerAllowsConsultCodes === true) {
    return result({
      code: '99241',
      action: 'keep',
      severity: 'info',
      message: '99241 is supported — legacy consult codes are enabled and payer allows consult codes.',
    });
  }
  return result({
    code: '99241',
    action: 'billing_review',
    severity: 'warning',
    message:
      '99241 requires legacy consult codes enabled and payer allowing consult codes. Flagging for billing review.',
  });
}

function evaluate99354Deprecated(): EncounterScrubberResult {
  return result({
    code: '99354',
    action: 'billing_review',
    severity: 'warning',
    message:
      '99354 was discontinued on 2023-01-01 and should not be auto-selected. Send to billing review.',
  });
}

function evaluateNotInUse(code: string): EncounterScrubberResult {
  return result({
    code,
    action: 'billing_review',
    severity: 'info',
    message: `${code} is not being used right now.`,
  });
}

const RULE_BY_CODE: Record<string, (input: EncounterScrubberInput) => EncounterScrubberResult> = {
  '90837': (input) => evaluatePsychotherapyDurationCode(input, '90837'),
  '90834': (input) => evaluatePsychotherapyDurationCode(input, '90834'),
  '90832': (input) => evaluatePsychotherapyDurationCode(input, '90832'),
  '90791': evaluate90791,
  '90792': evaluate90792,
  '90833': evaluate90833,
  '90839': evaluate90839,
  '90840': evaluate90840,
  '90846': evaluate90846,
  '90847': evaluate90847,
  '90853': evaluate90853,
  '96138': () => evaluate96138(),
  '96146': evaluate96146,
  '99241': evaluate99241,
  H0015: () => evaluateNotInUse('H0015'),
  Q3014: () => evaluateNotInUse('Q3014'),
};

for (const code of EM_CODES_NEW) {
  RULE_BY_CODE[code] = (input) => evaluateEmCode(input, code);
}
for (const code of EM_CODES_ESTABLISHED) {
  RULE_BY_CODE[code] = (input) => evaluateEmCode(input, code);
}

export function evaluateCptRules(input: EncounterScrubberInput): EncounterScrubberResult[] {
  const results: EncounterScrubberResult[] = [];
  const evaluated = new Set<string>();

  for (const code of input.currentCptCodes) {
    const normalized = code.trim().toUpperCase();
    if (!normalized || evaluated.has(normalized)) continue;
    evaluated.add(normalized);

    if (normalized === '99354') {
      results.push(evaluate99354Deprecated());
      continue;
    }

    const rule = RULE_BY_CODE[normalized];
    if (rule) {
      results.push(rule(input));
    }
  }

  const add96127 = evaluate96127(input, input.currentCptCodes.includes('96127'));
  if (add96127) results.push(add96127);

  const add90785 = evaluate90785(input, input.currentCptCodes.includes('90785'));
  if (add90785) results.push(add90785);

  if (
    input.currentCptCodes.includes('90839') &&
    input.crisisDocumentationPresent === true &&
    input.durationMinutes != null &&
    input.durationMinutes >= CRISIS_90840_FIRST_UNIT_MIN_MINUTES &&
    !input.currentCptCodes.includes('90840')
  ) {
    const units = calculate90840Units(input.durationMinutes);
    if (units > 0) {
      results.push(
        result({
          code: '90840',
          action: 'suggest_add',
          severity: 'info',
          message: `90840 can be added with ${units} unit${units === 1 ? '' : 's'} based on crisis duration.`,
          suggestedCode: '90840',
          units,
        }),
      );
    }
  }

  return results;
}
